// settings.js: persisted user settings (M2.c-8).
//
// One blob under localStorage['euchre_settings']. Defaults live here so
// every consumer agrees on shape + initial values without each having
// to re-derive them. Anything reading settings goes through
// loadSettings() so the merge with defaults happens in one place and
// stale keys from older versions are tolerated.
//
// Side-effect helpers (applyFontSetting) accept a `doc` parameter so
// Node tests can pass a stub document. In the browser the default
// global document is used.

const STORAGE_KEY = 'euchre_settings';

/** @typedef {'all'|'vital'|'off'} SoundTier */
/** @typedef {'off'|'subtle'|'full'} CrtMode */

/**
 * @typedef {{
 *   stick_the_dealer:       boolean,
 *   sound_tier:             SoundTier,
 *   reduced_motion_override: boolean,
 *   system_font:            boolean,
 *   heuristic_disclosure:   boolean,
 *   heuristic_hand_count:   number,
 *   crt:                    CrtMode,
 * }} Settings
 */

/** @type {Settings} */
export const DEFAULT_SETTINGS = Object.freeze({
  stick_the_dealer:        true,
  sound_tier:              'all',
  reduced_motion_override: false,
  system_font:             false,
  heuristic_disclosure:    true,
  heuristic_hand_count:    0,
  crt:                     'off',
});

const KEYS = /** @type {Array<keyof Settings>} */ (Object.keys(DEFAULT_SETTINGS));
const VALID_SOUND_TIER = new Set(['all', 'vital', 'off']);
const VALID_CRT = new Set(['off', 'subtle', 'full']);

function safeStorage() {
  try {
    if (typeof globalThis === 'undefined') return null;
    const s = /** @type {any} */ (globalThis).localStorage;
    if (!s || typeof s.getItem !== 'function') return null;
    return s;
  } catch {
    return null;
  }
}

/**
 * Coerce a partial settings object onto a base (defaults by default),
 * dropping any unknown keys and out-of-range enum values. Pure,
 * exported for tests. When `base` is provided, invalid patch entries
 * fall back to the base value rather than the default, so a corrupted
 * patch never silently resets a user's previously-valid setting.
 *
 * @param {Partial<Settings>|null|undefined} patch
 * @param {Settings} [base]
 * @returns {Settings}
 */
export function mergeSettings(patch, base) {
  /** @type {Settings} */
  // @ts-ignore, start from a fresh mutable copy
  const out = { ...(base || DEFAULT_SETTINGS) };
  if (!patch || typeof patch !== 'object') return out;
  for (const key of KEYS) {
    if (!(key in patch)) continue;
    const v = /** @type {any} */ (patch)[key];
    if (key === 'sound_tier') {
      if (VALID_SOUND_TIER.has(v)) out.sound_tier = v;
    } else if (key === 'crt') {
      if (VALID_CRT.has(v)) out.crt = v;
    } else if (key === 'heuristic_hand_count') {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
        out.heuristic_hand_count = Math.floor(v);
      }
    } else {
      if (typeof v === 'boolean') {
        // @ts-ignore, these keys are all booleans by shape
        out[key] = v;
      }
    }
  }
  return out;
}

/**
 * Read settings from localStorage, merge onto defaults. Always
 * returns a complete Settings object.
 *
 * @returns {Settings}
 */
export function loadSettings() {
  const s = safeStorage();
  if (!s) return mergeSettings(null);
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return mergeSettings(null);
    const parsed = JSON.parse(raw);
    return mergeSettings(parsed);
  } catch (err) {
    // W8: name the problem instead of swallowing it — a corrupt blob
    // falls back to defaults, but the user (and the log reader) should
    // know their saved settings did not apply.
    console.warn('[settings] corrupt JSON in localStorage, falling back to defaults:', err instanceof Error ? err.message : err);
    return mergeSettings(null);
  }
}

/**
 * Apply a partial update and persist. Returns the new full Settings.
 *
 * @param {Partial<Settings>} patch
 * @returns {Settings}
 */
export function saveSettings(patch) {
  const current = loadSettings();
  // Merge against `current` so invalid patch values preserve the user's
  // existing setting rather than snapping to default.
  const next = mergeSettings(patch, current);
  const s = safeStorage();
  if (s) {
    try { s.setItem(STORAGE_KEY, JSON.stringify(next)); }
    catch { /* quota / private mode, fall through, in-memory still works */ }
  }
  return next;
}

export function resetSettings() {
  const s = safeStorage();
  if (s) {
    try { s.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
  return mergeSettings(null);
}

import { applyCrtTier, effectiveTier, prefersReducedMotion } from './ui/crt-shader.js';

/**
 * Pure map from a settings object to the DOM classes the entry module
 * must set before the first paint (B2-12). The CRT effect and the
 * system font used to apply after the first render, so tests and users
 * saw the lobby before the class landed.
 *
 * @param {Partial<Settings>|null|undefined} settings
 * @returns {{ html: string[], body: string[] }}
 */
export function classesForSettings(settings) {
  const merged = mergeSettings(settings && typeof settings === 'object' ? settings : null);
  const tier = effectiveTier(merged.crt, false);
  return {
    html: tier === 'subtle' ? ['crt-subtle'] : tier === 'full' ? ['crt-full'] : [],
    body: merged.system_font ? ['system-font'] : [],
  };
}

/**
 * Read the saved settings and apply their DOM classes synchronously.
 * The entry module calls this before mounting so the classes are
 * present on the first frame. Returns the merged settings.
 *
 * @param {{
 *   storage?: { getItem: (k: string) => string|null }|null,
 *   doc?: Document|null,
 *   reducedMotion?: boolean,
 * }} [opts]
 * @returns {Settings}
 */
export function applySavedSettingsSync(opts = {}) {
  const storage = opts.storage !== undefined ? opts.storage : safeStorage();
  let parsed = null;
  try {
    const raw = storage && typeof storage.getItem === 'function'
      ? storage.getItem(STORAGE_KEY)
      : null;
    parsed = raw ? JSON.parse(raw) : null;
  } catch (err) {
    // W8: same corrupt-blob warning as loadSettings above.
    console.warn('[settings] corrupt JSON in localStorage, falling back to defaults:', err instanceof Error ? err.message : err);
    parsed = null;
  }
  const merged = mergeSettings(parsed && typeof parsed === 'object' ? parsed : null);
  const doc = opts.doc !== undefined
    ? opts.doc
    : (typeof document !== 'undefined' ? document : null);
  if (!doc) return merged;
  const reduced = opts.reducedMotion !== undefined ? opts.reducedMotion : prefersReducedMotion();
  applyCrtTier(effectiveTier(merged.crt, reduced), { doc: doc });
  applyFontSetting(merged.system_font, doc);
  applyReducedMotionSetting(reducedMotionActive(merged.reduced_motion_override, reduced), doc);
  return merged;
}

/**
 * Toggle the document body class that selects between the default
 * monospace/pixel font and the system fallback. Caller owns the boolean.
 *
 * @param {boolean} enabled  true = system font, false = pixel font
 * @param {Document} [doc]  optional override for tests
 */
export function applyFontSetting(enabled, doc) {
  const d = doc || (typeof document !== 'undefined' ? document : null);
  if (!d || !d.body) return;
  d.body.classList.toggle('system-font', !!enabled);
}

/**
 * Toggle the document body class that marks reduced motion, from either
 * the OS setting or the in-app override. cream-screens.css keys the
 * button-press shift and the phone hero float off body.reduce-motion
 * alone, and the desktop hero (HeroAnimation.jsx) stops its loop on it.
 *
 * @param {boolean} enabled  true = reduce motion
 * @param {Document} [doc]  optional override for tests
 */
export function applyReducedMotionSetting(enabled, doc) {
  const d = doc || (typeof document !== 'undefined' ? document : null);
  if (!d || !d.body) return;
  d.body.classList.toggle('reduce-motion', !!enabled);
}

/**
 * Reduced motion is on when either source asks for it: the in-app switch
 * or the OS setting.
 *
 * @param {boolean} override  settings.reduced_motion_override
 * @param {boolean} osReduced  prefers-reduced-motion: reduce
 * @returns {boolean}
 */
export function reducedMotionActive(override, osReduced) {
  return !!override || !!osReduced;
}

/**
 * Report the OS reduced-motion setting now and on every later change.
 * Returns the unsubscribe function. Outside a browser (no matchMedia)
 * there is no OS setting to read, so it reports false once.
 *
 * @param {(osReduced: boolean) => void} onChange
 * @param {{ matchMedia?: (q: string) => any }} [win]  optional override for tests
 * @returns {() => void}
 */
export function watchReducedMotion(onChange, win) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  if (!w || typeof w.matchMedia !== 'function') {
    onChange(false);
    return () => {};
  }
  const media = w.matchMedia('(prefers-reduced-motion: reduce)');
  const listener = (/** @type {{ matches: boolean }} */ e) => onChange(e.matches);
  onChange(media.matches);
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
}

export const _internals = { STORAGE_KEY, KEYS };
